import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface DashboardSettingsResponse {
  audio?: {
    pushToTalk?: { enabled?: boolean; hotkey?: string; hotkeyDisplay?: string };
    deviceId?: string;
    inputGain?: number;
    outputVolume?: number;
    visualsEnabled?: boolean;
    overlayScale?: number;
    onDemandVision?: boolean;
  };
  persona?: { visionInterjectionEnabled?: boolean; name?: string; voiceName?: string };
  triggers?: {
    commands?: { allowViewerMessages?: boolean; prefix?: string; modOnly?: boolean; cooldown?: number; sendCooldownMessages?: boolean };
    channelPoints?: { enabled?: boolean; rewardIds?: string[] };
  };
}

interface AudioSettings {
  pttEnabled: boolean;
  pttHotkey: string;
  pttHotkeyDisplay: string;
  micId: string;
  inputGain: number;
  outputVolume: number;
  isMuted: boolean;
  visualsEnabled: boolean;
  overlayScale: number;
}

interface CommandSettings { allowViewerMessages: boolean; prefix: string; modOnly: boolean; cooldown: number; sendCooldownMessages: boolean }
interface ChannelPointSettings { enabled: boolean; rewardIds: string[] }
interface PersonaSettings { assistantName: string; voiceName: string; onDemandVision: boolean }

export type DashboardSettingsChange =
  | { type: 'pttHotkey'; hotkey: string; display: string }
  | { type: 'mic'; deviceId: string }
  | { type: 'inputGain'; gain: number }
  | { type: 'volume'; volume: number }
  | { type: 'visualsEnabled'; enabled: boolean }
  | { type: 'overlayScale'; scale: number }
  | { type: 'allowViewerMessages'; enabled: boolean }
  | { type: 'commandPrefix'; prefix: string }
  | { type: 'modOnly'; enabled: boolean }
  | { type: 'cooldown'; seconds: number }
  | { type: 'sendCooldownMessages'; send: boolean }
  | { type: 'channelPointsEnabled'; enabled: boolean }
  | { type: 'channelPointRewardIds'; ids: string[] }
  | { type: 'assistantName'; name: string }
  | { type: 'personaVoiceName'; voiceName: string };

interface UseDashboardSettingsOptions {
  onError?: (message: string) => void;
  onReady?: () => void;
}

export interface DashboardSettingsResult {
  status: 'loading' | 'ready';
  audio: AudioSettings;
  commands: CommandSettings;
  channelPoints: ChannelPointSettings;
  persona: PersonaSettings;
  update: (change: DashboardSettingsChange) => void;
  toggleMute: () => void;
}

const DEFAULT_AUDIO: AudioSettings = { pttEnabled: true, pttHotkey: 'Backquote', pttHotkeyDisplay: '`', micId: '', inputGain: 1, outputVolume: 1, isMuted: false, visualsEnabled: true, overlayScale: 2 };
const DEFAULT_COMMANDS: CommandSettings = { allowViewerMessages: true, prefix: '!buddy', modOnly: false, cooldown: 5, sendCooldownMessages: true };
const DEFAULT_CHANNEL_POINTS: ChannelPointSettings = { enabled: false, rewardIds: [] };
const DEFAULT_PERSONA: PersonaSettings = { assistantName: 'Buddy', voiceName: 'Charon', onDemandVision: true };

function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

export function useDashboardSettings(options: UseDashboardSettingsOptions = {}): DashboardSettingsResult {
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');
  const [audio, setAudio] = useState<AudioSettings>(DEFAULT_AUDIO);
  const [commands, setCommands] = useState<CommandSettings>(DEFAULT_COMMANDS);
  const [channelPoints, setChannelPoints] = useState<ChannelPointSettings>(DEFAULT_CHANNEL_POINTS);
  const [persona, setPersona] = useState<PersonaSettings>(DEFAULT_PERSONA);
  const audioRef = useRef(audio);
  const previousVolumeRef = useRef(1);
  const micSaveTimeoutRef = useRef<number | null>(null);
  const onErrorRef = useRef(options.onError);
  const onReadyRef = useRef(options.onReady);
  onErrorRef.current = options.onError;
  onReadyRef.current = options.onReady;

  const updateAudio = useCallback((update: (current: AudioSettings) => AudioSettings) => {
    setAudio(current => {
      const next = update(current);
      audioRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch('/api/settings', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json() as DashboardSettingsResponse;
          const hydrated = {
            pttEnabled: data.audio?.pushToTalk ? true : DEFAULT_AUDIO.pttEnabled,
            pttHotkey: data.audio?.pushToTalk?.hotkey ?? DEFAULT_AUDIO.pttHotkey,
            pttHotkeyDisplay: data.audio?.pushToTalk?.hotkeyDisplay ?? DEFAULT_AUDIO.pttHotkeyDisplay,
            micId: data.audio?.deviceId ?? DEFAULT_AUDIO.micId,
            inputGain: data.audio?.inputGain ?? DEFAULT_AUDIO.inputGain,
            outputVolume: data.audio?.outputVolume ?? DEFAULT_AUDIO.outputVolume,
            isMuted: (data.audio?.outputVolume ?? DEFAULT_AUDIO.outputVolume) === 0,
            visualsEnabled: data.audio?.visualsEnabled ?? DEFAULT_AUDIO.visualsEnabled,
            overlayScale: data.audio?.visualsEnabled !== undefined ? (data.audio.overlayScale ?? DEFAULT_AUDIO.overlayScale) : DEFAULT_AUDIO.overlayScale,
          };
          audioRef.current = hydrated;
          previousVolumeRef.current = hydrated.outputVolume > 0 ? hydrated.outputVolume : 1;
          setAudio(hydrated);
          setCommands({
            allowViewerMessages: data.triggers?.commands?.allowViewerMessages ?? DEFAULT_COMMANDS.allowViewerMessages,
            prefix: data.triggers?.commands?.prefix ?? DEFAULT_COMMANDS.prefix,
            modOnly: data.triggers?.commands?.modOnly ?? DEFAULT_COMMANDS.modOnly,
            cooldown: data.triggers?.commands?.cooldown ?? DEFAULT_COMMANDS.cooldown,
            sendCooldownMessages: DEFAULT_COMMANDS.sendCooldownMessages,
          });
          setChannelPoints({ enabled: data.triggers?.channelPoints?.enabled ?? false, rewardIds: data.triggers?.channelPoints?.rewardIds ?? [] });
          setPersona({
            assistantName: data.persona?.name !== undefined ? (data.persona.name?.trim() || 'Buddy') : DEFAULT_PERSONA.assistantName,
            voiceName: data.persona?.voiceName !== undefined ? (data.persona.voiceName?.trim() || 'Charon') : DEFAULT_PERSONA.voiceName,
            onDemandVision: data.audio?.onDemandVision !== undefined ? true : DEFAULT_PERSONA.onDemandVision,
          });
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        onReadyRef.current?.();
        setStatus('ready');
      }
    };
    void fetchSettings();
  }, []);

  useEffect(() => {
    localStorage.setItem('streambuddy_ptt_enabled', String(audio.pttEnabled));
  }, [audio.pttEnabled]);

  const saveSettings = useCallback(async (body: unknown, errorMessage: string, logMessage: string) => {
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (csrfToken) headers['x-csrf-token'] = csrfToken;
      const res = await fetch('/api/settings', {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify(body),
      });
      if (!res.ok) onErrorRef.current?.(errorMessage);
    } catch (error) {
      console.error(logMessage, error);
      onErrorRef.current?.(errorMessage);
    }
  }, []);

  const scheduleMicSave = useCallback((deviceId: string, inputGain: number) => {
    if (micSaveTimeoutRef.current) window.clearTimeout(micSaveTimeoutRef.current);
    micSaveTimeoutRef.current = window.setTimeout(() => {
      micSaveTimeoutRef.current = null;
      void saveSettings({ audio: { deviceId, inputGain } }, 'Failed to save microphone settings', 'Failed to save microphone settings:');
    }, 150);
  }, [saveSettings]);

  useEffect(() => () => {
    if (micSaveTimeoutRef.current) window.clearTimeout(micSaveTimeoutRef.current);
  }, []);

  const setVolume = useCallback((outputVolume: number) => {
    if (outputVolume > 0) previousVolumeRef.current = outputVolume;
    updateAudio(current => ({ ...current, outputVolume, isMuted: outputVolume === 0 }));
    void saveSettings({ audio: { outputVolume } }, 'Failed to save volume setting', 'Failed to save volume setting:');
  }, [saveSettings, updateAudio]);

  const toggleMute = useCallback(() => {
    const current = audioRef.current;
    if (current.isMuted || current.outputVolume === 0) {
      setVolume(previousVolumeRef.current > 0 ? previousVolumeRef.current : 1);
    } else {
      previousVolumeRef.current = current.outputVolume;
      setVolume(0);
    }
  }, [setVolume]);

  const update = useCallback((change: DashboardSettingsChange) => {
    const command = <K extends keyof CommandSettings>(key: K, value: CommandSettings[K], wireKey: string, errorMessage: string, logMessage: string) => {
      setCommands(current => ({ ...current, [key]: value }));
      void saveSettings({ triggers: { commands: { [wireKey]: value } } }, errorMessage, logMessage);
    };
    switch (change.type) {
      case 'pttHotkey': updateAudio(c => ({ ...c, pttHotkey: change.hotkey, pttHotkeyDisplay: change.display })); void saveSettings({ audio: { pushToTalk: { enabled: audioRef.current.pttEnabled, hotkey: change.hotkey, hotkeyDisplay: change.display } } }, 'Failed to save PTT settings', 'Failed to save PTT settings:'); window.electronAPI?.onPTTHotkeyChanged(change.hotkey); break;
      case 'mic': updateAudio(c => ({ ...c, micId: change.deviceId })); scheduleMicSave(change.deviceId, audio.inputGain); break;
      case 'inputGain': updateAudio(c => ({ ...c, inputGain: change.gain })); scheduleMicSave(audio.micId, change.gain); break;
      case 'volume': setVolume(change.volume); break;
      case 'visualsEnabled': updateAudio(c => ({ ...c, visualsEnabled: change.enabled })); void saveSettings({ audio: { visualsEnabled: change.enabled } }, 'Failed to save visuals setting', 'Failed to save visuals setting:'); break;
      case 'overlayScale': updateAudio(c => ({ ...c, overlayScale: change.scale })); void saveSettings({ audio: { overlayScale: change.scale } }, 'Failed to save scale setting', 'Failed to save scale setting:'); break;
      case 'allowViewerMessages': command('allowViewerMessages', change.enabled, 'allowViewerMessages', 'Failed to save setting', 'Failed to save viewer message setting:'); break;
      case 'commandPrefix': { const prefix = change.prefix.startsWith('!') ? change.prefix : `!${change.prefix}`; command('prefix', prefix, 'prefix', 'Failed to save command prefix', 'Failed to save command prefix:'); break; }
      case 'modOnly': command('modOnly', change.enabled, 'modOnly', 'Failed to save setting', 'Failed to save mod only setting:'); break;
      case 'cooldown': command('cooldown', change.seconds, 'cooldown', 'Failed to save cooldown', 'Failed to save cooldown setting:'); break;
      case 'sendCooldownMessages': command('sendCooldownMessages', change.send, 'sendCooldownMessages', 'Failed to save setting', 'Failed to save sendCooldownMessages setting:'); break;
      case 'channelPointsEnabled': setChannelPoints(c => ({ ...c, enabled: change.enabled })); void saveSettings({ triggers: { channelPoints: { enabled: change.enabled } } }, 'Failed to save setting', 'Failed to save channel points setting:'); break;
      case 'channelPointRewardIds': setChannelPoints(c => ({ ...c, rewardIds: change.ids })); void saveSettings({ triggers: { channelPoints: { rewardIds: change.ids } } }, 'Failed to save reward IDs', 'Failed to save reward IDs setting:'); break;
      case 'assistantName': setPersona(c => ({ ...c, assistantName: change.name })); break;
      case 'personaVoiceName': setPersona(c => ({ ...c, voiceName: change.voiceName })); break;
    }
  }, [audio.inputGain, audio.micId, saveSettings, scheduleMicSave, setVolume, updateAudio]);

  return useMemo(() => ({ status, audio, commands, channelPoints, persona, update, toggleMute }), [audio, channelPoints, commands, persona, status, toggleMute, update]);
}
